import { useState } from "react";
import { Instagram, Loader2, Mail, MapPin, MessageCircle, Youtube } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { prettyHandle, prettyPhone, whatsappHref } from "@/lib/useSettings";
import { Reveal, SectionHeading } from "./Reveal";

const EVENT_TYPES = ["Wedding", "Pre-Wedding", "Portrait Session", "Commercial / Brand Film", "Documentary", "Other"];
const BUDGETS = ["Under ₹1 Lakh", "₹1 – 3 Lakh", "₹3 – 8 Lakh", "₹8 Lakh +", "Let's discuss"];

const buildInfo = (s) =>
  [
    { id: "email", icon: Mail, label: "Email", value: s.email, href: s.email ? `mailto:${s.email}` : "" },
    { id: "studios", icon: MapPin, label: "Studios", value: s.location, href: "" },
    { id: "whatsapp", icon: MessageCircle, label: "WhatsApp", value: prettyPhone(s.whatsapp), href: whatsappHref(s.whatsapp) },
    { id: "instagram", icon: Instagram, label: "Instagram", value: prettyHandle(s.instagram), href: s.instagram },
    { id: "youtube", icon: Youtube, label: "YouTube", value: prettyHandle(s.youtube), href: s.youtube },
  ].filter((row) => row.value);

const EMPTY = { name: "", email: "", event_type: EVENT_TYPES[0], shoot_date: "", budget: BUDGETS[4], message: "" };

export const Contact = ({ settings }) => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const info = buildInfo(settings);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill your name, email and a few words about the shoot.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Enquiry sent. Ansh will write back within 24 hours.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Could not send right now — please try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-28 sm:py-36">
      <div className="max-w-[1500px] mx-auto px-5 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <SectionHeading eyebrow="Commission a Story" title="Let's craft light together" testid="contact-heading" />
          <Reveal delay={0.15}>
            <p className="font-serif italic text-lg text-cream/85 mt-8 leading-relaxed">
              Dates for 2026 are now open. Tell me about the day, the people, the place —
              I will bring the light.
            </p>
          </Reveal>
          <div className="mt-12 space-y-7">
            {info.map((row, i) => {
              const Icon = row.icon;
              const Wrapper = row.href ? "a" : "div";
              const linkProps = row.href
                ? { href: row.href, target: row.href.startsWith("mailto:") ? undefined : "_blank", rel: "noreferrer" }
                : {};
              return (
                <Reveal key={row.id} delay={0.2 + i * 0.08}>
                  <Wrapper data-testid={`contact-info-${row.id}`} {...linkProps} className="flex items-center gap-5 group">
                    <span className="w-11 h-11 border border-line flex items-center justify-center text-ash group-hover:text-amber group-hover:border-amber/50 transition-all duration-300">
                      <Icon size={15} />
                    </span>
                    <div>
                      <p className="font-mono text-[9px] tracking-[0.35em] uppercase text-mute">{row.label}</p>
                      <p className={`font-mono text-sm text-ivory mt-1 ${row.href ? "group-hover:text-amber transition-colors" : ""}`}>{row.value}</p>
                    </div>
                  </Wrapper>
                </Reveal>
              );
            })}
          </div>
        </div>

        <Reveal delay={0.2} className="lg:col-span-7">
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="border border-ivory/10 bg-coal/70 backdrop-blur-xl p-7 sm:p-10"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-7">
              <div>
                <label className="font-mono text-[9px] tracking-[0.35em] uppercase text-mute">Your Name *</label>
                <input
                  data-testid="contact-form-input-name"
                  className="field mt-2"
                  placeholder="Aisha Kapoor"
                  value={form.name}
                  onChange={set("name")}
                />
              </div>
              <div>
                <label className="font-mono text-[9px] tracking-[0.35em] uppercase text-mute">Email *</label>
                <input
                  data-testid="contact-form-input-email"
                  type="email"
                  className="field mt-2"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={set("email")}
                />
              </div>
              <div>
                <label className="font-mono text-[9px] tracking-[0.35em] uppercase text-mute">Event Type</label>
                <select
                  data-testid="contact-form-select-category"
                  className="field mt-2 bg-coal"
                  value={form.event_type}
                  onChange={set("event_type")}
                >
                  {EVENT_TYPES.map((t) => (
                    <option key={t} value={t} className="bg-coal">{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="font-mono text-[9px] tracking-[0.35em] uppercase text-mute">Tentative Date</label>
                <input
                  data-testid="contact-form-input-date"
                  type="date"
                  className="field mt-2 [color-scheme:dark]"
                  value={form.shoot_date}
                  onChange={set("shoot_date")}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="font-mono text-[9px] tracking-[0.35em] uppercase text-mute">Estimated Budget</label>
                <select
                  data-testid="contact-form-select-budget"
                  className="field mt-2 bg-coal"
                  value={form.budget}
                  onChange={set("budget")}
                >
                  {BUDGETS.map((b) => (
                    <option key={b} value={b} className="bg-coal">{b}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="font-mono text-[9px] tracking-[0.35em] uppercase text-mute">About the Shoot *</label>
                <textarea
                  data-testid="contact-form-input-message"
                  rows={4}
                  className="field mt-2 resize-none"
                  placeholder="The venue, the people, the feeling you're after…"
                  value={form.message}
                  onChange={set("message")}
                />
              </div>
            </div>
            <button
              data-testid="contact-form-submit-button"
              type="submit"
              disabled={sending}
              className="mt-10 w-full sm:w-auto font-mono text-[11px] tracking-[0.3em] uppercase bg-ivory text-ink px-10 py-4 hover:bg-amber disabled:opacity-50 transition-colors duration-300 flex items-center justify-center gap-3"
            >
              {sending && <Loader2 size={14} className="animate-spin" />}
              {sending ? "Sending…" : "Send Enquiry"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
